import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import { Hammer, LogOut, MessageCircle, User, ShieldCheck, Bell } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <header className="glass-header sticky top-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <div className="w-9 h-9 rounded-full bg-terracotta flex items-center justify-center text-white">
            <Hammer className="w-4 h-4" strokeWidth={2} />
          </div>
          <span className="font-display text-xl font-semibold text-charcoal">Move Day Pro’s</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm text-charcoal/80">
          <Link to="/browse" className="hover:text-terracotta transition-colors" data-testid="nav-browse">Browse</Link>
          <Link to="/#how" className="hover:text-terracotta transition-colors" data-testid="nav-how">How it works</Link>
          {user?.role === "handyman" && (
            <Link to="/handyman" className="hover:text-terracotta transition-colors" data-testid="nav-handyman-dash">My Profile</Link>
          )}
          {user && (
            <Link to="/messages" className="hover:text-terracotta transition-colors flex items-center gap-1" data-testid="nav-messages">
              <MessageCircle className="w-4 h-4" /> Messages
            </Link>
          )}
          <Link to="/careers" className="hover:text-terracotta transition-colors" data-testid="nav-careers">Careers</Link>
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" className="text-sm text-terracotta hover:opacity-80 flex items-center gap-1" data-testid="nav-admin">
                  <ShieldCheck className="w-4 h-4" /> Admin
                </Link>
              )}
              <Link to="/dashboard" className="text-sm text-charcoal/80 hover:text-terracotta flex items-center gap-1" data-testid="nav-dashboard">
                <User className="w-4 h-4" /> {user.name?.split(" ")[0]}
              </Link>
              <Link to="/settings/notifications" className="text-charcoal/70 hover:text-terracotta transition-colors" title="Notification settings" data-testid="nav-notifications">
                <Bell className="w-4 h-4" />
              </Link>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="logout-btn"
                    className="rounded-full gap-1.5"
                    title="Sign out of all devices"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="hidden sm:inline">Sign out</span>
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="logout-confirm-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Sign out of all devices?</AlertDialogTitle>
                    <AlertDialogDescription>
                      You’ll be signed out here <span className="font-medium">and</span> on any other browser or device where you’re signed into Move Day Pro’s. You can sign back in anytime.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="logout-cancel-btn">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="logout-confirm-btn"
                      onClick={async () => { await logout(); nav("/"); }}
                      className="bg-terracotta hover:bg-terracotta/90 text-white"
                    >
                      Sign out everywhere
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          ) : (
            <>
              <Link to="/login"><Button variant="ghost" className="rounded-full" data-testid="nav-login">Sign in</Button></Link>
              <Link to="/register"><Button className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white btn-lift" data-testid="nav-signup">Get started</Button></Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
